import { Component, computed, effect, inject, OnInit, signal, untracked, input, ChangeDetectionStrategy } from '@angular/core';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { forkJoin } from 'rxjs';
import {
  PaginatedTableComponent,
  TableHeader,
} from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import {
  DateRangePickerOutput,
  DatePickerComponent,
} from '../../../../shared/components/date-picker/date-picker.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';

import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { ApiService } from '../../../../core/services/api.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { AiSummaryModalComponent } from '../../../../shared/components/ai-summary-modal/ai-summary-modal.component';
import { environment } from '../../../../../environments/environment';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { catchError, finalize, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { DashboardPageKey, IndustryCategorizationSectionId, IndustryCategorizationSectionDef } from '../../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilter, findCatalogPageId, resolveActiveCatalogProductId, stripProductPrefix } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';



interface AISummaryData {
  summary: string;
  insights: {
    label: string;
    evidence: string;
    value: string;
    severity: 'high' | 'med' | 'low';
  }[];
  playbook: {
    step: string;
    owner: string;
    timeframe: string;
    expected_change: string;
    effort?: string;
  }[];
  actions: string[];
  anomalies: string[];
}

enum IndustryCategoryType {
  HIGH_PROFIT_LOW_RISK = 0,
  HIGH_PROFIT_HIGH_RISK = 1,
  HIGH_OVERDUE_RISK = 2,
  HIGH_PROFIT_MODERATE_RISK = 3
}

enum CityCategoryType {
  HIGH_VALUED_CONTRACTS = 0,
  HIGH_VALUED_AMOUNT = 1
}

interface CategoryConfig {
  type: IndustryCategoryType | CityCategoryType;
  title: string;
  subtitle: string;
  titleColor: string;
  dataSignal: any;
  headers?: TableHeader[];
}

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: IndustryCategorizationSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor. Carries the
 *  section title alongside the persisted layout fields so the popup tile
 *  can render its label without re-looking-up `SECTIONS`. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

@Component({
  selector: 'app-industry-wise-categorization',
  templateUrl: './industry-wise-categorization.component.html',
  standalone: true,
  imports: [
    LayoutEditorOverlayDirective,
    PaginatedTableComponent,
    IndividualToasterComponent,
    DatePickerComponent,
    IconComponent,
    MatDialogModule,
    CommonModule,
    NgTemplateOutlet,
    DragDropModule,
    AiHeadlinesComponent,
    IconComponentV2,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host ::ng-deep app-paginated-table tbody td {
        text-align: left !important;
      }
      :host ::ng-deep app-paginated-table tbody td p {
        justify-content: flex-start !important;
      }
      
      .prism-ai-button {
        position: relative;
        background: transparent;
        border-radius: 4px;
        padding: 10px 24px;
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
        border: none;
      }
      .prism-ai-button::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 8px;
        padding: 2px; /* gradient border width */
        background: linear-gradient(90deg, #ad46ff, #35d86b, #2feff5);
        -webkit-mask: linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
      }

      .prism-ai-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(173, 70, 255, 0.2);
      }
    `,
  ],
})
export class IndustryWiseCategorizationComponent implements OnInit {
  private unitService = inject(UnitSuggestionService);
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private _dialog = inject(MatDialog);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.INDUSTRY_CATEGORIZATION;
  readonly SectionId = IndustryCategorizationSectionId;
  readonly SECTIONS = IndustryCategorizationSectionDef;

  selectedProductId = signal<string | null>(null);
  /** Tags in-flight fetches so stale responses are ignored after product changes. */
  private reportFetchGeneration = 0;
  unit = signal('day');
  sectionLoading = signal<Record<string, boolean>>({});
  isPrevious: boolean = false;

  start_date: any;
  end_date: any;

  aiSummaryData: AISummaryData = {
    summary: '',
    insights: [],
    playbook: [],
    actions: [],
    anomalies: [],
  };

  analysisModalVisible = false;
  analysisModalTitle = '';

  private AI_API_URLs: Record<string, { url: string; title: string }> = {
    generateSummary: {
      url: '/api/analyze/full-report',
      title: 'Full Report',
    },
  };

  openAnalysisModal(key: string) {
    this.aiSummaryData = {
      summary: '',
      insights: [],
      playbook: [],
      actions: [],
      anomalies: [],
    };
    let mapping = this.AI_API_URLs[key] || null;
    if (!mapping) {
      return;
    }

    this.analysisModalTitle = mapping.title;
    let payload = {
      start_time: this.start_date,
      end_time: this.end_date,
      // Use the user's groupBy selection (kept in `this.unit()`) so AI
      // analysis runs at the same granularity the dashboard is rendering.
      unit: this.unit() ||
        this.unitService.getUnitSuggestions(this.start_date, this.end_date)
          .recommended,
    };

    this.apiService
      .post(mapping.url, payload, {
        productServerName: environment.ai.productServerName,
        showLoader: true,
        showErrorToaster: true,
        errorMessage: 'Failed to get AI analysis.',
        timeout: 2 * 60 * 1000, // 2 minutes
      })
      .subscribe({
        next: (res: any) => {
          let summaryData: AISummaryData | null = null;
          if (res.summary_parsed) {
            summaryData = res;
          } else if (res.summary && typeof res.summary === 'string') {
            try {
              summaryData = JSON.parse(res.summary);
            } catch (e) {
              console.error('Failed to parse AI summary string:', e);
            }
          } else if (res.module_analyses) {
            summaryData = res.module_analyses;
          }
          if (summaryData) {
            this.aiSummaryData = summaryData;
            const data = { ...this.aiSummaryData };
            this._dialog.open(AiSummaryModalComponent, {
              width: '800px',
              data: key === 'generateSummary' ? this.aiSummaryData : [data],
            });
          } else {
            this.aiSummaryData.summary =
              'Could not parse the analysis from the AI response.';
          }
        },
        error: (err) => {
          console.error('AI analysis fetch error:', err);
        },
      });
  }

  constructor() {
    // React to global product selection changes
    effect(() => {
      const globalProductId = this.apiService.selectedProductId();
      if (globalProductId) {
        untracked(() => {
          if (this.selectedProductId() !== globalProductId) {
            this.reportFetchGeneration += 1;
          }
          this.selectedProductId.set(globalProductId);
          // Only fetch if dates have been initialized (handled by date picker usually, but safe to try if defaults exist)
          if (this.start_date && this.end_date) {
            this.fetchIndustryWiseCategorization();
            this.fetchCityWiseCategorization();
          }
        });
      }
    });

    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<IndustryCategorizationSectionId>();
          keys.forEach((k) => {
            if (Object.values(IndustryCategorizationSectionId).includes(k as IndustryCategorizationSectionId)) {
              pinned.add(k as IndustryCategorizationSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });
  }



  isSectionLoading(section: string): boolean {
    return this.sectionLoading()[section] || false;
  }

  setSectionLoading(section: string, isLoading: boolean): void {
    this.sectionLoading.update((prev) => ({ ...prev, [section]: isLoading }));
  }





  ngOnInit(): void {
    // Hydrate layout (visibility / colSpan / order) and pinned-section
    // state from localStorage. Replaces the old `sectionLayout.init` call —
    // we now manage layout locally so the page can also support per-card
    // resize + drag-drop reordering.
    this._initLayout();
    this.updateTableHeaders();
  }

  // ─── Section layout state (visibility + colSpan + order) ──────────────
  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<IndustryCategorizationSectionId, SectionLayoutItem>>(
    {} as Record<IndustryCategorizationSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the live grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilter(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.orderedSections,
  );

  /**
   * Sections currently rendered by the host. On the standalone page
   * (`mixedMode() === false`) every section is active. When mounted
   * inside `WidgetHostComponent` only the cards matching
   * `visibleWidgetKeys` are active — used to gate data loaders below.
   *
   * IMPORTANT: derived directly from `visibleWidgetKeys()` so it is
   * available synchronously on first paint; using the layout-derived
   * `embeddedVisibleSections()` would be empty before the layout loads
   * and would cause every guarded loader to short-circuit.
   */
  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const stripped = stripProductPrefix(k);
      if (stripped !== k) allowed.add(stripped);
    }
    return allowed;
  });

  private isSectionActive(...sectionIds: IndustryCategorizationSectionId[]): boolean {
    if (!this.mixedMode()) return true;
    const active = this.activeSectionIds();
    if (active.size === 0) return true;
    return sectionIds.some((id) => active.has(id));
  }

  isVisible(sectionId: IndustryCategorizationSectionId): boolean {
    return this.layoutMap()[sectionId]?.visible ?? true;
  }

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | IndustryCategorizationSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── "Pin to Custom Dashboard" (API-backed) ─────────────────────────────
  readonly pinnedSections = signal<Set<IndustryCategorizationSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<IndustryCategorizationSectionId>>(new Set());

  isAddedToCustomDashboard(sectionId: IndustryCategorizationSectionId): boolean {
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(sectionId: IndustryCategorizationSectionId): boolean {
    return this.pendingPinSections().has(sectionId);
  }

  addToCustomDashboard(
    sec: { id: IndustryCategorizationSectionId } | IndustryCategorizationSectionId,
  ): void {
    // Accept either the full section layout item (templates iterating over
    // `orderedSections()` pass `sec`) or the raw section id. Both flow
    // into the same `CustomDashboardPinService.toggleSection(...)` PATCH
    // request below.
    const sectionId =
      typeof sec === 'object' && sec !== null ? sec.id : sec;
    if (this.pendingPinSections().has(sectionId)) return;
    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned: this.isAddedToCustomDashboard(sectionId),
      onPinnedChange: (pinned) => {
        this.pinnedSections.update((s) => {
          const next = new Set(s);
          if (pinned) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
      onPendingChange: (pending) => {
        this.pendingPinSections.update((s) => {
          const next = new Set(s);
          if (pending) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  /** State for the inline Customize Layout editor. */
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  /** Number of cards currently marked visible in the editor draft. */
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_industry_categorization_layout_v1';

  /** Default col-span per section — both sections were full-width before
   *  so they both default to 12/12. */
  private readonly DEFAULT_COL_SPAN: Record<IndustryCategorizationSectionId, number> = {
    [IndustryCategorizationSectionId.INDUSTRY_CATEGORIZATION]: 12,
    [IndustryCategorizationSectionId.CITY_CATEGORIZATION]:     12,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
    }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: IndustryCategorizationSectionId): void {
    // When a card is hidden, push it to the bottom so the visible cards
    // reflow into the freed grid slot. When shown again, slot it back in
    // just above the first hidden item so it rejoins the visible group.
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  /** Restores the editor draft to the page's default order, widths, and visibility. */
  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.SECTIONS.map((s, idx) => ({
      id: s.id as IndustryCategorizationSectionId,
      title: s.title,
      visible: true,
      colSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as IndustryCategorizationSectionId] ?? 12,
      ),
      order: idx,
    }));
    this.editorItems.set(defaults);
  }

  /** Persists the editor draft to local layout state + localStorage. */
  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<IndustryCategorizationSectionId, SectionLayoutItem> =
      {} as Record<IndustryCategorizationSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence via the shared
    // `CustomizeLayoutBatchService` — same flow as Social Platform.
    // Service no-ops silently when `pageId` / catalog isn't ready or
    // the user has no mixed dashboard yet, so existing local-only
    // behaviour is preserved.
    const pageId = this.effectiveCatalogPageId();
    if (pageId != null) {
      this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
        pageId,
        productId: resolveActiveCatalogProductId(this.apiService),
        fallbackSourcePageId: pageId,
        draft: draft.map((d, idx) => ({
          key: String(d.id),
          sourcePageId: pageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        })),
      });
    }

    this.isLayoutEditorOpen.set(false);
  }

  // ─── Editor: width chips (decrease / increase / labels) ─────────────────
  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: IndustryCategorizationSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: IndustryCategorizationSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one card. */
  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(
    id: IndustryCategorizationSectionId,
    span: number,
  ): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  /** Tailwind col-span class for an editor tile inside the popup's 12-col grid. */
  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'col-span-4';
      case 6:  return 'col-span-6';
      case 8:  return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  /** Human-friendly label for a col-span value (Small / Medium / Large / Full). */
  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'Small';
      case 6:  return 'Medium';
      case 8:  return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

 /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }
  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<IndustryCategorizationSectionId, SectionLayoutItem> =
      {} as Record<IndustryCategorizationSectionId, SectionLayoutItem>;

    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as IndustryCategorizationSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<IndustryCategorizationSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  fetchIndustryWiseCategorization() {
    if (!this.isSectionActive(IndustryCategorizationSectionId.INDUSTRY_CATEGORIZATION)) return;

    this.setSectionLoading('industryWise', true);

    const productId = this.selectedProductId();
    if (!productId) {
       this.setSectionLoading('industryWise', false);
       return;
    }
    const fetchGeneration = this.reportFetchGeneration;
    const fetchProductId = productId;
    // this.productConfigService.ensureValidToken(this.selectedProductId()).subscribe({
    //   next: () => {
        forkJoin({
          t1: this.getIndustryObservable(0),
          t2: this.getIndustryObservable(1),
          t3: this.getIndustryObservable(2),
          t4: this.getIndustryObservable(3),
        }).subscribe({
          next: (res: any) => {
            const isStale = fetchGeneration !== this.reportFetchGeneration
              || fetchProductId !== this.selectedProductId();
            if (isStale) {
              this.setSectionLoading('industryWise', false);
              return;
            }
            this.data1.set(this.mapIndustryData(res.t1?.data || []));
            this.data2.set(this.mapIndustryData(res.t2?.data || []));
            this.data3.set(this.mapIndustryData(res.t3?.data || []));
            this.data4.set(this.mapIndustryData(res.t4?.data || []));
            this.setSectionLoading('industryWise', false);
          },
          error: (err) => {
            this.setSectionLoading('industryWise', false);
          },
        });
      // },
      // error: (err) => {
      //   this.setSectionLoading('industryWise', false);
      // },
    // });
  }

  fetchCityWiseCategorization() {
    if (!this.isSectionActive(IndustryCategorizationSectionId.CITY_CATEGORIZATION)) return;

    this.setSectionLoading('cityWise', true);

    const productId = this.selectedProductId();
    if (!productId) {
       this.setSectionLoading('cityWise', false);
       return;
    }
    const fetchGeneration = this.reportFetchGeneration;
    const fetchProductId = productId;
    // this.productConfigService.ensureValidToken(this.selectedProductId()).subscribe({
    //   next: () => {
        forkJoin({
          t1: this.getCityObservable(0),
          t2: this.getCityObservable(1),
        }).subscribe({
          next: (res: any) => {
            const isStale = fetchGeneration !== this.reportFetchGeneration
              || fetchProductId !== this.selectedProductId();
            if (isStale) {
              this.setSectionLoading('cityWise', false);
              return;
            }
            this.data5.set(this.mapCityData(res.t1?.data || [], 'Avg Total Contracts'));
            this.data6.set(this.mapCityData(res.t2?.data || [], 'Total Contracts'));
            this.setSectionLoading('cityWise', false);
          },
          error: (err) => {
            this.setSectionLoading('cityWise', false);
          },
        });
      // },
      // error: (err) => {
      //   this.setSectionLoading('cityWise', false);
      // },
    // });
  }

  private getIndustryObservable(type: number): Observable<any> {
    const productId = this.selectedProductId();
    if (!productId) return throwError(() => 'No Product Selected');

    const productName = this.productConfigService.getProductName(productId);
    const config = this.productConfigService.getProductConfig(productName);

    const payload = {
      start_time: this.start_date || '2025-09-30',
      end_time: this.end_date || '2025-12-30',
      type: type,
      is_dummy: 0,
      offset: 0,
    };

    const toolPath = this.productConfigService.getProductConfig(productName).reportToolsPath || `visionBoard/tools`;

    return this.apiService
      .post(`${toolPath}/sub_industries_contracts`, payload, {
        addAccessToken: config.requiresAccessToken,
        productServerName: config.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .pipe(
        catchError((err: any) => {
          if (
            err?.err === 'Invalid or expired access token' ||
            err?.message === 'Invalid or expired access token' ||
            err?.error === 'Invalid or expired access token'
          ) {
            return this.productConfigService.generateNewRefreshToken(productId).pipe(switchMap(() => this.getIndustryObservable(type)));
          }
          return throwError(() => err);
        })
      );
  }

  private getCityObservable(type: number): Observable<any> {
    const productId = this.selectedProductId();
    if (!productId) return throwError(() => 'No Product Selected');

    const productName = this.productConfigService.getProductName(productId);
    const config = this.productConfigService.getProductConfig(productName);

    const payload = {
      start_time: this.start_date || '2025-09-30',
      end_time: this.end_date || '2025-12-30',
      type: type,
      is_dummy: 0,
      offset: 0,
    };

    const toolPath = this.productConfigService.getProductConfig(productName).reportToolsPath || `visionBoard/tools`;

    return this.apiService
      .post(`${toolPath}/cities_contracts`, payload, {
        addAccessToken: config.requiresAccessToken,
        productServerName: config.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .pipe(
        catchError((err: any) => {
          if (
            err?.err === 'Invalid or expired access token' ||
            err?.message === 'Invalid or expired access token' ||
            err?.error === 'Invalid or expired access token'
          ) {
            return this.productConfigService.generateNewRefreshToken(productId).pipe(switchMap(() => this.getCityObservable(type)));
          }
          return throwError(() => err);
        })
      );
  }

  private mapIndustryData(data: any[]) {
    return data.map((item: any, index: number) => {
      const ratio = parseFloat(item.overdue_ratio);
      const overdueClass =
        ratio < 20
          ? 'bg-[#FFA51430] text-[#EE980E]' // Yellow
          : 'bg-[#F0505030] text-[#F05050]'; // Red

      return {
        '#': index + 1,
        'Sub Industry': item.speciality_name,
        Contracts: item.total_contracts,
        'Overdue %': item.overdue_ratio + '%',
        'Overdue %_class': overdueClass,
        'Avg Financed': '$' + item.average_financed_amount,
      };
    });
  }
  private mapCityData(data: any[], key: string) {
    return data.map((item: any, index: number) => {
      return {
        '#': index + 1,
        City: item.city,
        [key]: item.total_contracts,
        'Avg Financed': '$' + (item.average_financed_amount || 0),
      };
    });
  }

  // Table 1: High Profit and Low Risk
  headers1: TableHeader[] = [
    {
      label: '#',
      key: '#',
      align: 'text-center',
      sortable: false,
    },
    {
      label: 'Sub Industry',
      key: 'Sub Industry',
      align: 'text-center',
      sortable: true,
    },
    {
      label: 'Total Contracts',
      key: 'Contracts',
      align: 'text-center',
      sortable: true,
      cellType: 'badge',
      cellClass: 'bg-[#1877F226] text-[#0C66E8]',
    },
    {
      label: 'Overdue Ratio(%)',
      key: 'Overdue %',
      align: 'text-center',
      sortable: true,
      cellType: 'badge',
    },
    {
      label: 'Avg Financed',
      key: 'Avg Financed',
      align: 'text-center',
      transform: 'currency',
      sortable: true,
    },
  ];
  data1 = signal<any[]>([]);

  // Table 2: High Profit and High Risk
  // headers2: TableHeader[] = [
  //   {
  //     label: '#',
  //     key: '#',
  //     align: 'text-left',
  //     sortable: false,
  //   },
  //   {
  //     label: 'Sub Industry',
  //     key: 'Sub Industry',
  //     align: 'text-left',
  //     sortable: true,
  //   },
  //   {
  //     label: 'Total Contracts',
  //     key: 'Total Contracts',
  //     align: 'text-center',
  //     sortable: true,
  //     cellType: 'badge',
  //     cellClass: 'bg-[#1877F226] text-[#0C66E8]',
  //   },
  //   {
  //     label: 'Overdue Ratio(%)',
  //     key: 'Overdue Ratio(%)',
  //     align: 'text-center',
  //     sortable: true,
  //     cellType: 'badge',
  //   },
  //   {
  //     label: 'Avg Financed',
  //     key: 'Avg Financed',
  //     align: 'text-right',
  //     transform: 'currency',
  //     sortable: true,
  //   },
  // ];
  data2 = signal<any[]>([]);

  // Table 3: High Overdue Risk
  // headers3: TableHeader[] = [
  //   {
  //     label: '#',
  //     key: '#',
  //     align: 'text-left',
  //     sortable: false,
  //   },
  //   {
  //     label: 'Sub Industry',
  //     key: 'Sub Industry',
  //     align: 'text-left',
  //     sortable: true,
  //   },
  //   {
  //     label: 'Total Contracts',
  //     key: 'Total Contracts',
  //     align: 'text-center',
  //     sortable: true,
  //     isSortIconShow: false,
  //     cellType: 'badge',
  //     cellClass: 'bg-[#F0505030] text-[#F05050]',
  //   },
  //   {
  //     label: 'Overdue Ratio(%)',
  //     key: 'Overdue Ratio(%)',
  //     align: 'text-center',
  //     sortable: true,
  //     cellType: 'badge',
  //   },
  //   {
  //     label: 'Avg Financed',
  //     key: 'Avg Financed',
  //     align: 'text-right',
  //     transform: 'currency',
  //     sortable: true,
  //   },
  // ];
  data3 = signal<any[]>([]);

  // Table 4: High Profit and Moderate Overdue Risk
  headers4: TableHeader[] = [
    {
      label: '#',
      key: '#',
      align: 'text-center',
      sortable: false,
    },
    {
      label: 'Sub Industry',
      key: 'Sub Industry',
      align: 'text-center',
      sortable: true,
    },
    {
      label: 'Total Contracts',
      key: 'Contracts',
      align: 'text-center',
      sortable: true,
      cellType: 'badge',
      cellClass: 'bg-[#15B26830] text-[#15B268]',
    },
    {
      label: 'Overdue Ratio(%)',
      key: 'Overdue %',
      align: 'text-center',
      sortable: true,
      cellType: 'badge',
    },
    {
      label: 'Avg Financed',
      key: 'Avg Financed',
      align: 'text-center',
      transform: 'currency',
      sortable: true,
    },
  ];
  data4 = signal<any[]>([]);

  // Table 5: High Valued Cities (Contracts)
  headers5: TableHeader[] = [
    { label: '#', key: '#', align: 'text-center', sortable: false },
    { label: 'City', key: 'City', align: 'text-center', sortable: false },
    {
      label: 'Avg Total Contracts',
      key: 'Avg Total Contracts',
      align: 'text-center',
      sortable: true,
      isSortIconShow: false,
    },
  ];
  data5 = signal<any[]>([]);

  // Table 6: High Valued Cities (Amount)
  headers6: TableHeader[] = [
    { label: '#', key: '#', align: 'text-center', sortable: true },
    { label: 'City', key: 'City', align: 'text-center', sortable: true },
    {
      label: 'Total Contracts',
      key: 'Total Contracts',
      align: 'text-center',
      sortable: true,
      cellType: 'badge',
      cellClass: 'bg-[#1877F226] text-[#0C66E8]',
    },
    {
      label: 'Avg Financed',
      key: 'Avg Financed',
      align: 'text-center',
      transform: 'currency',
      sortable: true,
    },
  ];

  createTableHeaders(data: any[], previousData?: any[]): TableHeader[] {
    if (!data || !data.length) return [];
    const firstItem = data[0];

    return Object.keys(firstItem).map((key, index) => {
      let align: 'text-left' | 'text-center' | 'text-right';

      // if (index === 0) align = 'text-left'; // 1st column
      // else if (index === firstItem.length - 1)
      //   align = 'text-right'; // Last column
      align = 'text-center';
      return {
        label: this.formatHeaderLabel(key),
        key,
        align,
        isPrevious: previousData ? true : false,
      };
    }) as TableHeader[];
  }

  private formatHeaderLabel(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  tableHeaders6: TableHeader[] = [];

  private updateTableHeaders(): void {
    const headers6: TableHeader[] = [
      { label: '#', key: '#', align: 'text-center', sortable: false },
      {
        label: 'Sub Industry',
        key: 'City',
        align: 'text-center',
        sortable: false,
      },
      {
        label: 'Total Contracts',
        key: 'Total Contracts',
        align: 'text-center',
        sortable: true,
        cellType: 'badge',
        cellClass: 'bg-[#1877F226] text-[#0C66E8]',
      },
      {
        label: 'Contracts',
        key: 'Contracts',
        align: 'text-center',
        sortable: true,
        cellType: 'badge',
        cellClass: 'bg-[#1877F226] text-[#0C66E8]',
      },
      {
        label: 'Avg Financed',
        key: 'Avg Financed',
        align: 'text-center',
        transform: 'currency',
        showProgress: false,
        sortable: true,
      },
    ];

    // Only add previous column if previous dates are selected
    // if (this.previousData6?.length > 0) {
    //   headers6.push({
    //     label: `Previous`,
    //     key: 'Avg Financed Amount',
    //     align: 'text-left',
    //     isPrevious: false,
    //   });
    // }

    this.tableHeaders6 = headers6;

    // Update cardsData with new table headers
  }

  data6 = signal<any[]>([]);
  previousData6 = [
    {
      City: 'Jacksonville',
      'Total Contracts': 260,
      'Avg Financed Amount': 3453,
    },
    {
      City: 'Los Angeles County',
      'Total Contracts': 222,
      'Avg Financed Amount': 2423,
    },
    { City: 'Atlanta', 'Total Contracts': 145, 'Avg Financed Amount': 5654 },
    { City: 'Detroit', 'Total Contracts': 13, 'Avg Financed Amount': 23 },
    { City: 'Metairie', 'Total Contracts': 126, 'Avg Financed Amount': 7563 },
    {
      City: 'Greenwood Village',
      'Total Contracts': 42,
      'Avg Financed Amount': 6355,
    },
    { City: 'Huntsville', 'Total Contracts': 89, 'Avg Financed Amount': 1234 },
    { City: 'New York', 'Total Contracts': 30, 'Avg Financed Amount': 7543 },
  ];

  rangeValues(event: any) {}

  onDateRangeChange(event: DateRangePickerOutput) {
    const { start_date, end_date, unit } = event;

    this.start_date = dayjs.utc(start_date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    this.end_date = dayjs.utc(end_date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    this.unit.set(unit);
    // Honor the user's current groupBy selection (kept in `this.unit()` by the
    // `unit.set(unit)` above) instead of re-deriving the granularity from the
    // date range. Previously this collapsed long ranges back to the
    // suggestion service's recommended bucket (e.g. "month" for 6m–1y) and
    // ignored explicit picks like Quarter / Year.
    let payload = {
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit() ||
        this.unitService.getUnitSuggestions(this.start_date, this.end_date)
          .recommended,
      overall: 1,
    };
    this.fetchIndustryWiseCategorization();
    this.fetchCityWiseCategorization();
    // Use the data for API calls or other logic
    // if (previous_start_date && previous_end_date) {

    // } else {

    // }
  }

  donutData = computed(() => {
    return [
      {
        label: 'Consultant',
        value: 213,
        color: '#3B82F6',
      },
      {
        label: 'Financial Edu.',
        value: 5465,
        color: '#10B981',
      },
      {
        label: 'Institute',
        value: 234,
        color: '#8B5CF6',
      },
      {
        label: 'Tax Res.',
        value: 3423,
        color: '#F59E0B',
      },
      {
        label: 'Others',
        value: 678,
        color: '#EF4444',
      },
    ];
  });

  chartData = [
    { label: 'Jacksonville', value: 360 },
    { label: 'LA County', value: 245 },
    { label: 'Las Vegas', value: 216 },
    { label: 'Atlanta', value: 150 },
    { label: 'Metairie', value: 50 },
  ];

  businessWiseContractChartData: any = {
    chartData: this.chartData,
  };
  previousChartData = [
    { label: 'Jacksonville', value: 360 },
    { label: 'LA County', value: 245 },
    { label: 'Las Vegas', value: 216 },
    { label: 'Atlanta', value: 150 },
    { label: 'Metairie', value: 50 },
  ];

  previousBusinessWiseContractChartData: any = {
    chartData: this.previousChartData,
  };

  colors: any = {
    // data1: '#a78bfa' as `#${string}` // blue-500 for bars
    data1: '#0C66E8' as `#${string}`, // blue-500 for bars
  };

  // Industry categories configuration
  industryCategories = computed<CategoryConfig[]>(() => [
    {
      type: IndustryCategoryType.HIGH_PROFIT_LOW_RISK,
      title: 'High Profit and Low Risk',
      subtitle: 'By Financed Amount',
      titleColor: 'text-[#0C66E8]',
      dataSignal: this.data1,
      headers: this.headers1
    },
    {
      type: IndustryCategoryType.HIGH_PROFIT_HIGH_RISK,
      title: 'High Profit and High Risk',
      subtitle: 'By Financed Amount',
      titleColor: 'text-[#F05050]',
      dataSignal: this.data2,
      headers: this.headers1
    },
    {
      type: IndustryCategoryType.HIGH_OVERDUE_RISK,
      title: 'High Overdue Risk',
      subtitle: 'By Overdue Ratio(%)',
      titleColor: 'text-[#FFA514]',
      dataSignal: this.data3,
      headers: this.headers1
    },
    {
      type: IndustryCategoryType.HIGH_PROFIT_MODERATE_RISK,
      title: 'High Profit and Moderate Overdue Risk',
      subtitle: 'By Financed Amount',
      titleColor: 'text-[#15B268]',
      dataSignal: this.data4,
      headers: this.headers4
    }
  ]);

  // City categories configuration
  cityCategories = computed<CategoryConfig[]>(() => [
    {
      type: CityCategoryType.HIGH_VALUED_CONTRACTS,
      title: 'High Valued Cities',
      subtitle: 'By Total Contracts',
      titleColor: 'text-[#0C66E8]',
      dataSignal: this.data5,
      headers: this.headers5
    },
    {
      type: CityCategoryType.HIGH_VALUED_AMOUNT,
      title: 'High Valued Cities',
      subtitle: 'By Financed Amount',
      titleColor: 'text-[#15B268]',
      dataSignal: this.data6,
      headers: this.tableHeaders6
    }
  ]);

  hasNoIndustryWiseCategorizationData = computed(() => {
    return (
      this.data1().length == 0 &&
      this.data2().length == 0 &&
      this.data3().length == 0 &&
      this.data4().length == 0
    );
  });

  hasNoCityWiseCategorizationData = computed(() => {
    return this.data5().length == 0 && this.data6().length == 0;
  });
}
